/**
 * Body progress, headless — the Body tab's header marks and denominators
 * (Body-tab XD note 2026-09-27 §3.1, build plan PR 2).
 *
 * WHAT THIS IS. Every Body object (a hand, the elbows, a zone, the tail
 * spring, a soft mass) mapped onto the six status states, per section, with
 * the count a folded header reads: "2 of 3", or "1 suggested" when nothing is
 * settled yet but the engine has advice. The mapping is the note's table,
 * row by row; body-progress.test.ts pins every cell.
 *
 * THE RULES THE COUNT KEEPS:
 *  - It counts OBJECTS, never controls ("beauty is a denominator", brief §5.1).
 *  - SETTLED means `yours` or `fine`. A proposal is never promoted to done
 *    (brief §5.6: score and provenance are never merged).
 *  - A TOUCHED section is COMPLETE (Allen, 2026-09-28): once a person decides
 *    one object in it (`yours`), leaving the rest alone is a valid choice — set
 *    the chest, not the glutes. The untouched objects keep their own marks,
 *    drawn faint, and nothing about them asks again. A `check` holds the
 *    section open: that is the evidence disagreeing, not a choice skipped.
 *  - An object the body does not support is ABSENT — not drawn, not counted
 *    (no disabled promises).
 *  - Hue only where the object has a twin marker on the model: a hand wears the
 *    wrist's hue, the elbows the elbow's. Zones and soft masses are neutral.
 *
 * PURE: no DOM, no three.js. main.ts fills the readings from data the panels
 * already read; body-panel.ts draws the result.
 * Depends on: status-mark (the state type only). Depended on by: body-panel, main.
 */
import type { StatusState } from './status-mark.js';

// ---- the readings (what main.ts can fill without new state) ----

/** A hand's pins while it is being pinned (the hands panel's live UI state). */
export interface HandLiveReading {
  /** `none` = no pins yet; `proposed` = a pre-filled name nobody confirmed; `named` = every name a person's (or unnamed). */
  readonly pins: 'none' | 'proposed' | 'named';
  /** A pin the volume does not corroborate (the row's `⚠`). */
  readonly unsupported: boolean;
}

export interface HandReading {
  readonly side: 'left' | 'right';
  /** The rig hand has no digits of its own (the graft is on offer). */
  readonly graftable: boolean;
  /** The Document holds a person's confirmed pins for this hand (it was grafted from them). */
  readonly savedPins: boolean;
  /** The panel's live pinning state, when the hand has been opened this session. */
  readonly live: HandLiveReading | null;
}

export interface ElbowPinReading {
  readonly confirmed: boolean;
  /** The joint reads off-centre in the measured section (the button's `data-suspect`). */
  readonly suspect: boolean;
  /** A person moved it and has not pinned it (a draft). */
  readonly placed: boolean;
  /** The placement or the pin was refused (`pin.error`, not the rough-station note). */
  readonly refused: boolean;
}

/** The elbows as ONE object; `null` or no pins = the body offers no elbow correction (absent). */
export interface ElbowsReading {
  readonly pins: readonly ElbowPinReading[];
}

export interface ZoneReading {
  readonly name: string;
  readonly intent: string | null;
  /** The zone's flag carries a refusal (a chain that would not grow, and the like). */
  readonly refused: boolean;
}

export interface ZoneOfferReading {
  readonly shape: 'tube' | 'sheet' | 'blob' | 'stub';
}

export interface TailSpringReading {
  /** A chain exists and can be sprung. */
  readonly available: boolean;
  /** Why a grown tail could not be sprung, or null. */
  readonly refusal: string | null;
  readonly on: boolean;
  /** Where the settings come from: a person (`document`), the file, or the prior's defaults. */
  readonly settingsFrom: 'document' | 'file' | 'prior';
  /** A collider was dropped or shrunk to clear the tail. */
  readonly collidersAdjusted: boolean;
}

export type SoftCardState = 'offered' | 'quiet' | 'grown' | 'file' | 'unmeasured' | 'native';

export interface SoftMassReading {
  /** The mass a person names: Chest, Belly, Glutes. */
  readonly label: string;
  /** Its per-side cards (SoftTissueCardView.state + tier). */
  readonly cards: readonly { readonly state: SoftCardState; readonly tier: 'strong' | 'weak' | 'none' | null }[];
}

/** ADR-039: the Hair card's state (HairCardView.state); null = nothing to offer (absent). */
export type HairReadingState = 'refused' | 'offered' | 'dismissed' | 'confirmed' | 'stale' | null;

export interface BodyReadings {
  /** The hands tool is offered at all (body-panel's `showHands`). */
  readonly handsOffered: boolean;
  readonly hands: readonly HandReading[];
  readonly elbows: ElbowsReading | null;
  /** Null = the zones reading refused or is not offered (no zone objects). */
  readonly zones: { readonly zones: readonly ZoneReading[]; readonly offers: readonly ZoneOfferReading[] } | null;
  readonly tailSpring: TailSpringReading | null;
  readonly softOffered: boolean;
  readonly soft: readonly SoftMassReading[];
  /** Absent (undefined) = the app offers no Hair card. */
  readonly hair?: HairReadingState;
  /** ADR-040: the World check's cards, already marked (intake-view); absent = no intake session. */
  readonly intake?: readonly ProgressObject[];
}

// ---- the result ----

export interface ProgressObject {
  readonly subject: string;
  readonly state: StatusState;
  /** The object's identity hue, only where it has a twin marker on the model. */
  readonly hue?: string;
  /** The weak tier: drawn at half strength (§3.1, soft mass "weak tier = suggested at 50 %"). */
  readonly faint?: boolean;
}

export interface SectionProgress {
  readonly objects: readonly ProgressObject[];
  readonly settled: number;
  readonly total: number;
  /** Everything settled, or a person touched it (one `yours`) and nothing reads `check`. */
  readonly complete: boolean;
}

export interface BodyProgress {
  readonly intake: SectionProgress;
  readonly joints: SectionProgress;
  readonly parts: SectionProgress;
  readonly hair: SectionProgress;
  readonly soft: SectionProgress;
}

const HUE_WRIST = 'var(--hue-wrist)';
const HUE_ELBOW = 'var(--hue-elbow)';

/** Settled = a person decided (`yours`) or there is nothing to do (`fine`). */
export const isSettled = (state: StatusState): boolean => state === 'yours' || state === 'fine';

// ---- the §3.1 mapping, one function per row ----

/**
 * Hand: needs = graftable, no pins · proposed = pins pre-named, not confirmed ·
 * yours = grafted from a person's pins · fine = fingered rig, no saved state ·
 * check = a pin `⚠` unsupported. (suggested: none.)
 */
export function handState(h: HandReading): StatusState {
  if (h.live?.unsupported === true) return 'check';
  if (h.live?.pins === 'proposed') return 'proposed';
  if (h.savedPins) return 'yours';
  if (!h.graftable) return 'fine';
  // Graftable, not grafted: no pins, or a person's pins still short of a graft — the open question.
  return 'needs';
}

/**
 * Elbows: suggested = `data-suspect` · proposed = placed, not pinned ·
 * yours = both pinned · fine = not suspect, untouched · check = a pin refused.
 * (needs: none.) Null = absent.
 */
export function elbowsState(e: ElbowsReading): StatusState | null {
  if (e.pins.length === 0) return null;
  if (e.pins.some((p) => p.refused)) return 'check';
  const open = e.pins.filter((p) => !p.confirmed);
  if (open.some((p) => p.placed)) return 'proposed';
  if (open.some((p) => p.suspect)) return 'suggested';
  // Both pinned, or one pinned and the other measured fine: a person decided where it mattered.
  return e.pins.some((p) => p.confirmed) ? 'yours' : 'fine';
}

/**
 * Zone: needs = pinned, intent unanswered · yours = intent answered ·
 * check = a refusal on the flag. (suggested is the OFFER, below.)
 */
export function zoneState(z: ZoneReading): StatusState {
  if (z.refused) return 'check';
  return z.intent === null ? 'needs' : 'yours';
}

/** Zone offer: the detector's offer — and the stub's tentative one — is `suggested`. */
export function zoneOfferState(_o: ZoneOfferReading): StatusState {
  return 'suggested';
}

/**
 * Tail spring: suggested = chain grown, spring never touched · proposed = on
 * "from the tail prior" · yours = a person set mood or muscle · fine = on "as
 * the file declares" · check = colliders dropped or shrunk. Null = absent.
 *
 * The prior is ON (`PRIOR_TAIL_SPRING.on`), so an untouched grown chain always
 * reads "on from the prior" = proposed; `suggested` is reached only if the
 * prior is ever off. A spring REFUSAL (a grown tail that could not be sprung)
 * has no row; it maps to `check` — the chain stands, the spring could not.
 */
export function tailSpringState(t: TailSpringReading): StatusState | null {
  if (!t.available) return t.refusal !== null ? 'check' : null;
  if (t.collidersAdjusted) return 'check';
  if (t.settingsFrom === 'document') return 'yours';
  if (t.settingsFrom === 'file') return 'fine';
  return t.on ? 'proposed' : 'suggested';
}

/**
 * Soft mass: suggested = offered (strong; weak = suggested at 50 %) ·
 * yours = grown by a person · fine = native, or grown `in file` ·
 * check = unmeasured. A QUIET mass (measured, nothing to offer) is absent.
 * A mass is its sides together; the first match in this order wins, the same
 * order the card's summary reads (live, offered, native, else).
 */
export function softMassState(m: SoftMassReading): { state: StatusState; faint: boolean } | null {
  const has = (s: SoftCardState) => m.cards.some((c) => c.state === s);
  if (has('grown')) return { state: 'yours', faint: false };
  if (has('file')) return { state: 'fine', faint: false };
  if (has('offered')) {
    const strong = m.cards.some((c) => c.state === 'offered' && c.tier === 'strong');
    return { state: 'suggested', faint: !strong };
  }
  if (has('native')) return { state: 'fine', faint: false };
  if (has('unmeasured')) return { state: 'check', faint: false };
  return null;
}

/**
 * Hair (ADR-039): suggested = offered · yours = confirmed, or set aside (a
 * person answered — nothing recorded, nothing asked again) · check = not
 * measured, or confirmed on another mesh. Null = absent.
 */
export function hairState(h: HairReadingState): StatusState | null {
  if (h === null) return null;
  if (h === 'offered') return 'suggested';
  if (h === 'confirmed' || h === 'dismissed') return 'yours';
  return 'check';
}

function section(objects: readonly ProgressObject[]): SectionProgress {
  const settled = objects.filter((o) => isSettled(o.state)).length;
  const touched = objects.some((o) => o.state === 'yours') && !objects.some((o) => o.state === 'check');
  const complete = objects.length > 0 && (settled === objects.length || touched);
  // In a touched section the rest was left as is on purpose: faint, never promoted.
  const drawn = complete ? objects.map((o) => (isSettled(o.state) ? o : { ...o, faint: true })) : objects;
  return { objects: drawn, settled, total: objects.length, complete };
}

export function bodyProgress(r: BodyReadings): BodyProgress {
  const joints: ProgressObject[] = [];
  if (r.handsOffered) {
    for (const side of ['left', 'right'] as const) {
      for (const h of r.hands.filter((x) => x.side === side)) {
        joints.push({ subject: side === 'left' ? 'Left hand' : 'Right hand', state: handState(h), hue: HUE_WRIST });
      }
    }
  }
  const elbows = r.elbows === null ? null : elbowsState(r.elbows);
  if (elbows !== null) joints.push({ subject: 'Elbows', state: elbows, hue: HUE_ELBOW });

  const parts: ProgressObject[] = [];
  if (r.zones !== null) {
    for (const z of r.zones.zones) parts.push({ subject: z.name, state: zoneState(z) });
    for (const o of r.zones.offers) parts.push({ subject: OFFER_SUBJECT[o.shape], state: zoneOfferState(o) });
  }
  const tail = r.tailSpring === null ? null : tailSpringState(r.tailSpring);
  if (tail !== null) parts.push({ subject: 'Tail spring', state: tail });

  const soft: ProgressObject[] = [];
  if (r.softOffered) {
    for (const m of r.soft) {
      const s = softMassState(m);
      if (s !== null) soft.push({ subject: m.label, state: s.state, ...(s.faint ? { faint: true } : {}) });
    }
  }
  const hair: ProgressObject[] = [];
  const h = r.hair === undefined ? null : hairState(r.hair);
  if (h !== null) hair.push({ subject: 'Hair', state: h });
  return { intake: section(r.intake ?? []), joints: section(joints), parts: section(parts), hair: section(hair), soft: section(soft) };
}

const OFFER_SUBJECT: Readonly<Record<ZoneOfferReading['shape'], string>> = {
  tube: 'Tail?',
  sheet: 'Cloth?',
  blob: 'Region?',
  stub: 'Stub tail?',
};

/**
 * The header's count: "complete" once the section is (all settled, or touched);
 * "2 of 3"; "1 suggested" when nothing is settled, nothing needs you, and the
 * engine has advice; "" for a section with no objects.
 */
export function progressCount(p: SectionProgress): string {
  if (p.total === 0) return '';
  if (p.complete) return 'complete';
  const suggested = p.objects.filter((o) => o.state === 'suggested').length;
  const needs = p.objects.some((o) => o.state === 'needs');
  if (p.settled === 0 && suggested > 0 && !needs) return `${suggested} suggested`;
  return `${p.settled} of ${p.total}`;
}
